import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { 
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Avatar,
  Divider
} from "@material-ui/core";
import {
  Menu as MenuIcon,
  AccountCircle,
  ExitToApp,
  Home as HomeIcon,
  ContactMail as ContactIcon,
  ListAlt as ListIcon
} from "@material-ui/icons";
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  appBar: {
    background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    boxShadow: 'none',
  },
  logo: {
    height: 40,
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem',
    },
  },
  navButton: {
    marginLeft: theme.spacing(2),
    color: 'white',
    textTransform: 'none',
    fontWeight: 'normal',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  mobileMenu: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  desktopMenu: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  userAvatar: {
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
    width: 30,
    height: 30,
  },
  menuPaper: {
    minWidth: 200,
    padding: theme.spacing(1),
  },
}));

const Header = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isLogin, setIsLogin] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileAnchorEl, setMobileAnchorEl] = useState(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userDetails = await axios.get("/auth/userDetails");
        const currentPath = window.location.pathname;
        
        if (userDetails.data) {
          setIsLogin(userDetails.data);
          
          if (currentPath === "/login" || currentPath === "/register") {
            if (userDetails?.data?.accountType === "business") {
              history.push("/businessDash");
            }
          }
        } else if (currentPath === "/login" || currentPath === "/register" || currentPath === "/") {
          history.push("/login");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        history.push("/login");
      }
    };
    fetchUserDetails();
  }, [history]);

  const handleLogout = async () => {
    await axios.get("/auth/logout");
    setIsLogin(null);
    history.push("/login");
    window.location.reload();
  };

  const handleProfile = () => {
    if (isLogin?.accountType === "business") {
      history.push("/businessDash");
    } else {
      history.push("/");
    }
    handleCloseMenus();
  };

  const handleSymbolList = () => {
    history.push("/Symbol");
    handleCloseMenus();
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileAnchorEl(event.currentTarget);
  };

  const handleCloseMenus = () => {
    setAnchorEl(null);
    setMobileAnchorEl(null);
  };

  const renderDesktopMenu = () => (
    <div className={classes.desktopMenu}>
      <Button 
        className={classes.navButton} 
        startIcon={<HomeIcon />}
        href="https://www.tradingandcoffee.com/" 
        target="_blank"
      >
        Home
      </Button>
      <Button 
        className={classes.navButton} 
        startIcon={<ContactIcon />}
        href="/contact"
      >
        Contact
      </Button>
      <Button 
        className={classes.navButton} 
        startIcon={<ListIcon />}
        onClick={handleSymbolList}
      >
        Symbol List
      </Button>

      {isLogin ? (
        <>
          <Button
            className={classes.navButton}
            startIcon={<AccountCircle />}
            onClick={handleMenuOpen}
          >
            {isLogin.firstName}
          </Button>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleCloseMenus}
            PaperProps={{ className: classes.menuPaper }}
          >
            <MenuItem onClick={handleProfile}>
              <Box display="flex" alignItems="center">
                <Avatar className={classes.userAvatar}>
                  {isLogin.firstName.charAt(0)}
                </Avatar>
                <Box ml={1}>
                  <Typography variant="subtitle1">{isLogin.firstName}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {isLogin.accountType === "business" ? "Business Account" : "Personal Account"}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ExitToApp fontSize="small" style={{ marginRight: 8 }} />
              Logout
            </MenuItem>
          </Menu>
        </>
      ) : (
        <>
          <Button 
            className={classes.navButton} 
            href="/login"
          >
            Log in
          </Button>
          <Button 
            className={classes.navButton} 
            href="/register"
            variant="outlined"
            color="inherit"
          >
            Register
          </Button>
        </>
      )}
    </div>
  );

  const renderMobileMenu = () => (
    <>
      <div className={classes.mobileMenu}>
        <IconButton
          edge="end"
          color="inherit"
          aria-label="menu"
          onClick={handleMobileMenuOpen}
        >
          <MenuIcon />
        </IconButton>
      </div>
      
      <Menu
        anchorEl={mobileAnchorEl}
        keepMounted
        open={Boolean(mobileAnchorEl)}
        onClose={handleCloseMenus}
        PaperProps={{ className: classes.menuPaper }}
      >
        <MenuItem 
          component="a" 
          href="https://www.tradingandcoffee.com/" 
          target="_blank"
        >
          <HomeIcon fontSize="small" style={{ marginRight: 8 }} />
          Home
        </MenuItem>
        <MenuItem 
          component="a" 
          href="/contact"
        >
          <ContactIcon fontSize="small" style={{ marginRight: 8 }} />
          Contact
        </MenuItem>
        <MenuItem onClick={handleSymbolList}>
          <ListIcon fontSize="small" style={{ marginRight: 8 }} />
          Symbol List
        </MenuItem>

        {isLogin ? (
          [
            <MenuItem key="profile" onClick={handleProfile}>
              <AccountCircle fontSize="small" style={{ marginRight: 8 }} />
              Profile ({isLogin.firstName})
            </MenuItem>,
            <MenuItem key="logout" onClick={handleLogout}>
              <ExitToApp fontSize="small" style={{ marginRight: 8 }} />
              Logout
            </MenuItem>
          ]
        ) : (
          [
            <MenuItem 
              key="login" 
              component="a" 
              href="/login"
            >
              Log in
            </MenuItem>,
            <MenuItem 
              key="register" 
              component="a" 
              href="/register"
            >
              Register
            </MenuItem>
          ]
        )}
      </Menu>
    </>
  );

  return (
    <AppBar position="static" className={classes.appBar}>
      <Toolbar>
        <img 
          src="/Logo.jpg" 
          alt="Logo" 
          className={classes.logo} 
          onClick={() => history.push("/")}
          style={{ cursor: 'pointer' }}
        />
        
        <Typography variant="h6" className={classes.title}>
          Trading & Coffee
        </Typography>

        {renderDesktopMenu()}
        {renderMobileMenu()}
      </Toolbar>
    </AppBar>
  );
};

export default Header;